import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, AlertCircle, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PageLayout } from '../components/Layout';
import { apiClient, useAuth } from '../lib/api';

export const GuideClaimPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'eligible' | 'not_eligible' | 'already_owned' | 'error' | 'redirecting'
  const [offerDetails, setOfferDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // Not logged in - redirect to login with return path
      navigate('/login', { state: { from: '/guide/claim' } });
      return;
    }

    // Check guide offer eligibility
    checkEligibilityAndRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const checkEligibilityAndRedirect = async () => {
    try {
      const response = await apiClient.getGuideOfferStatus();
      const data = response.data;
      setOfferDetails(data);

      if (data.has_guide) {
        setStatus('already_owned');
        return;
      }

      if (data.eligible) {
        // Eligible! Start checkout automatically
        setStatus('redirecting');
        await startCheckout();
      } else {
        setStatus('not_eligible');
      }
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to check offer eligibility');
      setStatus('error');
    }
  };

  const startCheckout = async () => {
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.createGuideCheckout(originUrl, true); // true = use discount
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setErrorMessage('Unable to start checkout');
        setStatus('error');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'You already own this guide') {
        setStatus('already_owned');
      } else {
        setErrorMessage(detail || 'Failed to start checkout');
        setStatus('error');
      }
    }
  };

  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    checkEligibilityAndRedirect();
  };

  if (authLoading || status === 'loading') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#0B1D51] animate-spin mx-auto mb-4" />
            <p className="text-[#64748B]">Checking your offer eligibility...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (status === 'redirecting') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#FF9F1C] animate-spin mx-auto mb-4" />
            <p className="text-[#0B1D51] font-semibold text-lg">Redirecting to checkout...</p>
            <p className="text-[#64748B] mt-2">You'll be taken to our secure payment page.</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#0B1D51] to-[#1a3a8f] p-6 text-center">
              <BookOpen className="w-12 h-12 text-white mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-white">TDF Psychometric Guide</h1>
              <p className="text-white/80 mt-1">Subscriber Discount Offer</p>
            </div>

            <CardContent className="p-6">
              {status === 'already_owned' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#0B1D51] mb-2">You Already Own This Guide!</h2>
                  <p className="text-[#64748B] mb-6">
                    Great news - you've already purchased the TDF Psychometric Guide. 
                    You can access it anytime from your dashboard.
                  </p>
                  <Link to="/dashboard">
                    <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}

              {status === 'not_eligible' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#0B1D51] mb-2">Offer Not Available</h2>
                  <p className="text-[#64748B] mb-4">
                    This special discount is exclusively for active monthly subscribers.
                  </p>
                  
                  {offerDetails && (
                    <div className="bg-slate-50 rounded-lg p-4 text-left mb-6">
                      <p className="text-sm text-[#64748B]">
                        <strong>Why am I seeing this?</strong>
                      </p>
                      <ul className="text-sm text-[#64748B] mt-2 space-y-1 list-disc list-inside">
                        {!user?.is_member && <li>You need an active subscription to qualify</li>}
                        {offerDetails.dismissed && <li>You previously dismissed this offer</li>}
                        {offerDetails.days_since_subscription !== null && offerDetails.days_since_subscription < offerDetails.offer_delay_days && (
                          <li>Offer unlocks after {offerDetails.offer_delay_days} days of subscription ({offerDetails.offer_delay_days - offerDetails.days_since_subscription} days remaining)</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {!user?.is_member && (
                      <Link to="/pricing">
                        <Button className="bg-[#FF9F1C] hover:bg-[#e8900a]">
                          Subscribe Now
                        </Button>
                      </Link>
                    )}
                    <Link to="/dashboard">
                      <Button variant="outline">
                        Go to Dashboard
                      </Button>
                    </Link>
                  </div>

                  {offerDetails?.dismissed && (
                    <p className="text-xs text-[#64748B] mt-4">
                      If you dismissed the offer by mistake, please contact support to have it restored.
                    </p>
                  )}
                </div>
              )}

              {status === 'error' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#0B1D51] mb-2">Something Went Wrong</h2>
                  <p className="text-[#64748B] mb-6">{errorMessage || 'Please try again or contact support.'}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={handleRetry} className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
                      Try Again
                    </Button>
                    <Link to="/dashboard">
                      <Button variant="outline">
                        Go to Dashboard
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#64748B] mt-6">
            Need help? <a href="mailto:help@traindriverpsychometrics.co.uk" className="text-[#0B1D51] underline">Contact Support</a>
          </p>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default GuideClaimPage;
